import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as sppoCloseCreationAPI from '../../api/SpPOAPI/sppoCloseCreationAPI';

// Async Thunks
// =============================================

export const fetchSPPOCCforClose = createAsyncThunk(
    'sppoCloseCreation/fetchSPPOCCforClose',
    async ({ userId, roleId }, { rejectWithValue }) => {
        try { return await sppoCloseCreationAPI.getSPPOCCforClose(userId, roleId); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Centers'); }
    }
);

export const fetchVendorsForSPPOClose = createAsyncThunk(
    'sppoCloseCreation/fetchVendorsForSPPOClose',
    async (ccCode, { rejectWithValue }) => {
        try { return await sppoCloseCreationAPI.getVendorsForSPPOClose(ccCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Vendors'); }
    }
);

export const fetchPOForSPPOClose = createAsyncThunk(
    'sppoCloseCreation/fetchPOForSPPOClose',
    async ({ vendorCode, ccCode }, { rejectWithValue }) => {
        try { return await sppoCloseCreationAPI.getPOForSPPOClose(vendorCode, ccCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch PO Numbers'); }
    }
);

export const fetchSPPOByNoForClose = createAsyncThunk(
    'sppoCloseCreation/fetchSPPOByNoForClose',
    async ({ sppono, ccCode, vendorCode }, { rejectWithValue }) => {
        try { return await sppoCloseCreationAPI.getSPPOByNoForClose(sppono, ccCode, vendorCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch PO Details'); }
    }
);

export const closeSPPO = createAsyncThunk(
    'sppoCloseCreation/closeSPPO',
    async (payload, { rejectWithValue }) => {
        try { return await sppoCloseCreationAPI.closeSPPO(payload); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to close SPPO'); }
    }
);

// Initial State
// =============
const initialState = {
    costCenters: [],
    vendors: [],
    poNumbers: [],
    poDetails: null,

    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        costCenters: false,
        vendors: false,
        poNumbers: false,
        poDetails: false,
        save: false,
    },

    errors: {
        costCenters: null,
        vendors: null,
        poNumbers: null,
        poDetails: null,
        save: null,
    },
};

const sppoCloseCreationSlice = createSlice({
    name: 'sppoCloseCreation',
    initialState,
    reducers: {
        clearVendors: (state) => { state.vendors = []; state.errors.vendors = null; },
        clearPONumbers: (state) => { state.poNumbers = []; state.errors.poNumbers = null; },
        clearPODetails: (state) => { state.poDetails = null; state.errors.poDetails = null; },
        clearSaveResult: (state) => { state.saveResult = null; state.saveStatus = null; state.errors.save = null; },
        resetSPPOClose: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSPPOCCforClose.pending, (state) => { state.loading.costCenters = true; state.errors.costCenters = null; })
            .addCase(fetchSPPOCCforClose.fulfilled, (state, action) => { state.loading.costCenters = false; state.costCenters = action.payload?.Data || []; })
            .addCase(fetchSPPOCCforClose.rejected, (state, action) => { state.loading.costCenters = false; state.errors.costCenters = action.payload; state.costCenters = []; });

        builder
            .addCase(fetchVendorsForSPPOClose.pending, (state) => { state.loading.vendors = true; state.errors.vendors = null; })
            .addCase(fetchVendorsForSPPOClose.fulfilled, (state, action) => { state.loading.vendors = false; state.vendors = action.payload?.Data || []; })
            .addCase(fetchVendorsForSPPOClose.rejected, (state, action) => { state.loading.vendors = false; state.errors.vendors = action.payload; state.vendors = []; });

        builder
            .addCase(fetchPOForSPPOClose.pending, (state) => { state.loading.poNumbers = true; state.errors.poNumbers = null; })
            .addCase(fetchPOForSPPOClose.fulfilled, (state, action) => { state.loading.poNumbers = false; state.poNumbers = action.payload?.Data || []; })
            .addCase(fetchPOForSPPOClose.rejected, (state, action) => { state.loading.poNumbers = false; state.errors.poNumbers = action.payload; state.poNumbers = []; });

        builder
            .addCase(fetchSPPOByNoForClose.pending, (state) => { state.loading.poDetails = true; state.errors.poDetails = null; })
            .addCase(fetchSPPOByNoForClose.fulfilled, (state, action) => { state.loading.poDetails = false; state.poDetails = action.payload?.Data || null; })
            .addCase(fetchSPPOByNoForClose.rejected, (state, action) => { state.loading.poDetails = false; state.errors.poDetails = action.payload; state.poDetails = null; });

        builder
            .addCase(closeSPPO.pending, (state) => { state.loading.save = true; state.errors.save = null; state.saveStatus = 'pending'; })
            .addCase(closeSPPO.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;
                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                if (responseStr === 'Submited') { state.saveStatus = 'success'; }
                else { state.saveStatus = 'failed'; state.errors.save = responseStr || 'Save failed'; }
            })
            .addCase(closeSPPO.rejected, (state, action) => { state.loading.save = false; state.errors.save = action.payload; state.saveStatus = 'failed'; state.saveResult = null; });
    },
});

export const {
    clearVendors,
    clearPONumbers,
    clearPODetails,
    clearSaveResult,
    resetSPPOClose,
} = sppoCloseCreationSlice.actions;

// Selectors
// =========
const arr = (x) => (Array.isArray(x) ? x : []);

export const selectCostCentersArray = (state) => arr(state.sppoCloseCreation.costCenters);
export const selectCostCentersLoading = (state) => state.sppoCloseCreation.loading.costCenters;

export const selectVendorsArray = (state) => arr(state.sppoCloseCreation.vendors);
export const selectVendorsLoading = (state) => state.sppoCloseCreation.loading.vendors;

export const selectPONumbersArray = (state) => arr(state.sppoCloseCreation.poNumbers);
export const selectPONumbersLoading = (state) => state.sppoCloseCreation.loading.poNumbers;

export const selectPODetails = (state) => state.sppoCloseCreation.poDetails;
export const selectPODetailsLoading = (state) => state.sppoCloseCreation.loading.poDetails;

export const selectSaveStatus = (state) => state.sppoCloseCreation.saveStatus;
export const selectSaveLoading = (state) => state.sppoCloseCreation.loading.save;
export const selectSaveError = (state) => state.sppoCloseCreation.errors.save;

export default sppoCloseCreationSlice.reducer;
